import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import '../components/cos-icon.js';

/**
 * <cos-empty-state> — Stage 70.1 standardized empty-state card.
 *
 * Replaces the ~30-40 hand-rolled `class="empty"` inline blocks scattered
 * across section cards, list pages, and "all caught up" surfaces. Centered
 * vertical stack: optional icon, title, optional description, optional
 * action CTA.
 *
 * Props:
 *   - title: string — main heading (required)
 *   - description?: string — optional secondary text
 *   - tone: 'default' | 'quiet' (default 'default'). 'quiet' drops the
 *     icon and tightens padding, for use inside a section card where the
 *     card is already the chrome.
 *   - headingLevel: 'h2' | 'h3' (default 'h2'). Use 'h3' inside section
 *     cards so the document outline stays valid.
 *
 * Slots:
 *   - action — optional primary CTA button(s)
 *   - icon — optional custom icon (overrides the default; suppressed in
 *     'quiet' tone)
 *
 * Consumer migration of the existing inlines is each surface's own later
 * stage — this primitive just lands the standard.
 */
@customElement('cos-empty-state')
export class CosEmptyState extends LitElement {
	// `title` is a global HTML attribute, so an authored `title="..."`
	// binding also lands a native `title` attribute on the host, and the
	// browser then paints its own tooltip on hover, duplicating the
	// heading this component already renders underneath it. The
	// accessor pair below captures the value for rendering and strips
	// the attribute right back off the host so the browser never sees
	// it. `_strippingTitle` guards against `removeAttribute` re-entering
	// this setter with an empty string, which would otherwise blank the
	// heading we just captured. Do not simplify this back to a plain
	// `@property`, that is the bug.
	//
	// The strip is gated on `isConnected`, and that gate is load-bearing,
	// not cosmetic. lit-html caches one `<template>` per call site and
	// clones it on every render; the very first "render" of that
	// template, the one that parses the static `title="..."` into the
	// template's own master node, happens before that node is ever
	// inserted anywhere, so it is not connected. Stripping the attribute
	// there would mutate the shared master node itself, and every future
	// clone (i.e. every future render of that call site, anywhere in the
	// app) would clone a node that never had the attribute to begin
	// with, silently blanking the title for every consumer after the
	// first. Real browsers never upgrade or run callbacks on elements
	// still living inside an inert `<template>.content`, so this can
	// only bite in a DOM implementation that upgrades early, but the
	// gate costs nothing in a spec-correct browser (an element that
	// isn't connected isn't visible, so there is no tooltip to remove
	// yet) and removes the corruption risk either way. `connectedCallback`
	// re-runs the same strip for the rare case where the element gained
	// the attribute while still disconnected and was connected afterward
	// with no further attribute mutation to re-trigger the setter.
	private _title = '';
	private _strippingTitle = false;

	@property({ type: String })
	get title(): string {
		return this._title;
	}

	set title(value: string) {
		if (this._strippingTitle) return;
		const previous = this._title;
		this._title = value ?? '';
		this.requestUpdate('title', previous);
		this._stripTitleAttribute();
	}

	connectedCallback() {
		super.connectedCallback();
		this._stripTitleAttribute();
	}

	private _stripTitleAttribute() {
		if (!this.isConnected || !this.hasAttribute('title')) return;
		this._strippingTitle = true;
		this.removeAttribute('title');
		this._strippingTitle = false;
	}

	@property({ type: String }) description = '';
	@property({ type: String }) tone: 'default' | 'quiet' = 'default';
	@property({ type: String }) headingLevel: 'h2' | 'h3' = 'h2';

	static styles = css`
		:host {
			display: block;
		}
		.wrap {
			display: flex;
			flex-direction: column;
			align-items: center;
			text-align: center;
			gap: var(--cos-space-2);
			max-width: 480px;
			margin: 0 auto;
			padding: var(--cos-space-8) var(--cos-space-4);
		}
		:host([tone='quiet']) .wrap {
			padding: var(--cos-space-5) var(--cos-space-3);
		}
		.icon {
			color: var(--cos-text-tertiary);
			margin-bottom: var(--cos-space-1);
		}
		.title {
			margin: 0;
			font-size: var(--cos-font-size-base);
			font-weight: var(--cos-font-weight-medium);
			color: var(--cos-text-primary);
		}
		.desc {
			margin: 0;
			font-size: var(--cos-font-size-sm);
			color: var(--cos-text-secondary);
			line-height: 1.5;
		}
		.action {
			margin-top: var(--cos-space-3);
		}
		/* Hide the action wrapper entirely when nothing is slotted, so the
		   margin doesn't leave a gap. */
		slot[name='action']::slotted(*) {
			display: inline-block;
		}
	`;

	private _renderHeading() {
		// Heading level is configurable for a valid document outline; render
		// the matching native element rather than aria-level on a <div>.
		return this.headingLevel === 'h3'
			? html`<h3 class="title">${this.title}</h3>`
			: html`<h2 class="title">${this.title}</h2>`;
	}

	render() {
		const showIcon = this.tone !== 'quiet';
		return html`
			<div class="wrap">
				${showIcon
					? html`<span class="icon"
							><slot name="icon"><cos-icon name="list" size="32"></cos-icon></slot
						></span>`
					: ''}
				${this._renderHeading()}
				${this.description ? html`<p class="desc">${this.description}</p>` : ''}
				<div class="action"><slot name="action"></slot></div>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'cos-empty-state': CosEmptyState;
	}
}
